import React, { useMemo, useState } from 'react';
import { Camp } from '../../utils/types';
import { useAddCampToSchedule, useRemoveCampFromSchedule, useCreateBooking } from '../../api';
import './CampCard.css';
import { useCampContext } from '../../context/CampContext';
import { useUserContext } from '../../context/UserContext';
import { LoginModalProvider } from '../../context/LoginModalContext';
import LoginModal from '../login/LoginModal';
import { useNotifications } from '../../context/NotificationContext';
import CampDatesDropdown from './CampDatesDropdown';
import { Calendar, CalendarCheck, Clock, DollarSign, ExternalLink, Heart, Users, Zap } from 'lucide-react';
import { CampFormatUtils } from '../../utils/campFormatUtils';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { useNewsletterModalState } from '../../hooks/useNewsletterModalState';
import GoogleRating from '../GoogleRating';
import { useAnalytics } from '../../context/AnalyticsContext';
import { useFeatureFlag } from '../../context/FeatureFlagContext';
import { FeatureFlags } from '../../utils/featureFlags';
import NewsletterSignupModal from '../NewsletterSignupModal';
import CampTag from './CampTag';
import { useNavigate } from 'react-router-dom';

interface CampCardProps {
	camp: Camp;
	selectedDate?: string;
	onDateChange?: (campId: string, dateId: string) => void;
	onScheduleAdd?: (beginDate: Date) => void;
	favoriteId?: string;
	scheduledDateIds?: string[];
	newsletterSignupEnabled?: boolean;
	onCampClick?: (camp: Camp) => void;
	onMouseEnter?: (organizerId?: number) => void;
	onMouseLeave?: () => void;
	scheduleUuidOverride?: string | null; // Use this schedule UUID instead of currentScheduleUuid
}

const CampCard: React.FC<CampCardProps> = ({
	camp,
	selectedDate,
	onDateChange,
	onScheduleAdd,
	favoriteId,
	scheduledDateIds,
	newsletterSignupEnabled = false,
	onCampClick,
	onMouseEnter,
	onMouseLeave,
	scheduleUuidOverride
}) => {
	const [showRegistrationModal, setShowRegistrationModal] = useState(false);
	const [showNewsletterModal, setShowNewsletterModal] = useState(false);
	const addCampToSchedule = useAddCampToSchedule();
	const removeCampFromSchedule = useRemoveCampFromSchedule();
	const createBooking = useCreateBooking();
	const { setSchedule, currentScheduleUuid } = useCampContext();
	const { isLoggedIn } = useUserContext();
	const { showSuccess, showError } = useNotifications();
	const { trackEvent } = useAnalytics();
	const { isInFavorites, handleFavoriteToggle, isPending } = useFavoriteToggle();
	const { shouldShowModal, markAsShown, markAsSignedUp } = useNewsletterModalState();
	const navigate = useNavigate();
	const instantBookingEnabled = useFeatureFlag(FeatureFlags.INSTANT_BOOKING, false);

	// Use override schedule UUID if provided, otherwise use current schedule UUID
	const effectiveScheduleUuid = scheduleUuidOverride ?? currentScheduleUuid;

	const handleRegister = (email: string) => {
		setShowRegistrationModal(false)
		showSuccess("You have successfully registered! You can now add favorites and add camps to your schedule.")
	};

	const handleLogin = (email: string) => {
		setShowRegistrationModal(false)
		showSuccess("You have successfully logged in! You can now add favorites and add camps to your schedule.")
	};

	const handleFavoriteClick = (e: React.MouseEvent, camp: Camp, favoriteId: string) => {
		e.stopPropagation();
		trackEvent('results_page_camp_card_save_clicked', { camp_id: camp.id, is_logged_in: isLoggedIn });
		if (!isLoggedIn) {
			setShowRegistrationModal(true);
			return;
		}
		handleFavoriteToggle(camp, favoriteId);
	};

	const processBooking = async () => {
		// Use instant booking flow only if feature flag is enabled AND camp supports it
		const useInstantBooking = instantBookingEnabled && camp.organizer?.instant_booking;

		if (!useInstantBooking) {
			if (camp.website) {
				window.open(camp.website, '_blank', 'noopener,noreferrer');
			}
			return;
		}

		try {
			const campDateIds = effectiveSelectedDate
				? [effectiveSelectedDate]
				: camp.camp_dates.map(cd => String(cd.id));
			const booking = await createBooking.mutateAsync({
				camp_id: camp.id,
				camp_date_ids: campDateIds
			});
			navigate(`/checkout?booking_uuid=${booking.uuid}`, { state: { booking } });
		} catch (error) {
			showError('Failed to create booking. Please try again.', error as Error);
		}
	};

	const handleBookNow = async (e: React.MouseEvent) => {
		e.stopPropagation();
		trackEvent('results_page_camp_card_book_now_clicked', { camp_id: camp.id });

		// Skip newsletter modal for instant booking camps
		const useInstantBooking = instantBookingEnabled && camp.organizer?.instant_booking;
		if (!isLoggedIn && newsletterSignupEnabled && shouldShowModal() && !useInstantBooking) {
			setShowNewsletterModal(true);
			return;
		}

		await processBooking();
	};

	const handleNewsletterSignup = async (email: string) => {
		setShowNewsletterModal(false);
		markAsSignedUp();
		trackEvent('camp_card_newsletter_signup_book_now', { camp_id: camp.id});
		showSuccess('Thank you for subscribing to our newsletter!');
		await processBooking();
	};

	const handleSkipNewsletter = async () => {
		setShowNewsletterModal(false);
		markAsShown();
		trackEvent('camp_card_newsletter_skip_book_now', { camp_id: camp.id });
		await processBooking();
	};

	const handleNewsletterModalClose = () => {
		setShowNewsletterModal(false);
		markAsShown();
		trackEvent('camp_card_newsletter_modal_closed', { camp_id: camp.id });
	};

	// Disabled for MVP
	const handleScheduleToggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (!isLoggedIn) {
			setShowRegistrationModal(true);
			return;
		}
		if (!effectiveScheduleUuid) {
			showError('Something went wrong. Please try again later.', new Error('No schedule selected'));
			return;
		}
		if (isEffectivelyScheduled) {
			removeCampFromSchedule.mutate(
				{ scheduleUuid: effectiveScheduleUuid, campId: camp.id, campDateId: effectiveSelectedDate },
				{
					onSuccess: (data) => {setSchedule(data); showSuccess('Camp removed from schedule'); },
					onError: (error) => {showError('Failed to remove camp from schedule. Please try again.', error); }
				}
		);
		} else {
			addCampToSchedule.mutate(
				{ scheduleUuid: effectiveScheduleUuid, campId: camp.id, campDateId: effectiveSelectedDate },
				{ 
					onSuccess: (data) => {
					setSchedule(data); 
					showSuccess('Camp added to schedule');
					
					// Find the specific camp date that was added
					const addedCamp = data.scheduled_camps.find(
						(scheduledCamp: any) => 
							scheduledCamp.camp.id === camp.id && 
							scheduledCamp.camp_date.id === effectiveSelectedDate
					);
					
					if (addedCamp?.camp_date?.start_date) {
						// Parse the date string and call the callback
						const beginDate = new Date(addedCamp.camp_date.start_date);
						onScheduleAdd?.(beginDate);
					}
				},
				onError: (error) => {showError('Failed to add camp to schedule. Please try again.', error); }
			});
		}
	};

	const effectiveSelectedDate: string = useMemo(() => {
		if (selectedDate) return String(selectedDate);
		if (camp.camp_dates.length === 1) return String(camp.camp_dates[0].id);
		return '';
	}, [selectedDate, camp.camp_dates]);

	const isEffectivelyScheduled = Boolean(effectiveSelectedDate && scheduledDateIds?.map(x => String(x))?.includes(effectiveSelectedDate));

	const selectedCampDate = useMemo(() => {
		return camp.camp_dates.find(cd => String(cd.id) === effectiveSelectedDate);
	}, [camp.camp_dates, effectiveSelectedDate]);

	// Note: current_capacity is the number of bookings already made, so available = max_capacity - current_capacity
	const isAtCapacity = Boolean(selectedCampDate && selectedCampDate.max_capacity - selectedCampDate.current_capacity <= 0);

	function getCampImage(camp: Camp): string {
		return camp.images.find(image => image.type === 'default')?.url || '/images/camp-card-default-image.png';
	}

	const priceParts = CampFormatUtils.getPriceParts(camp);
	const isTopRated = camp.organizer?.google_rating && camp.organizer?.google_rating > 4.5 &&
	                    camp.organizer?.google_rating_count && camp.organizer?.google_rating_count > 20;
	const isPopular = camp.bookings >= 4;
  const isPromoted = camp.promoted;

	return (
		<div
			className="camp-card"
			onMouseEnter={() => onMouseEnter?.(camp.organizer?.id)}
			onMouseLeave={() => onMouseLeave?.()}
		>
			{/* Image Section */}
			<div className="camp-image-container">
				{isPopular && <CampTag text="Popular" variant="popular" />}
        {isPromoted && <CampTag text="Promoted" variant="promoted" />}
				{isTopRated && !isPopular && <CampTag text="Top Rated" variant="rated" />}
				{instantBookingEnabled && camp.organizer?.instant_booking && <CampTag text="Direct Booking" variant="instant_booking" />}

				<button
					className='camp-favorite-button'
					aria-label={isInFavorites(camp) ? 'Remove from favorites' : 'Add to favorites'}
					title={isInFavorites(camp) ? 'Remove from favorites' : 'Add to favorites'}
					onClick={(e) => handleFavoriteClick(e, camp, '1')}
					disabled={isPending}
				>
					<Heart className="icon icon--sm" fill={isInFavorites(camp) ? '#E44E4E' : 'white'} stroke={isInFavorites(camp) ? '#B33030' : '#6b7280'} strokeWidth={1} />
				</button>

				<img
					src={getCampImage(camp)}
					alt={camp.name}
					className="camp-image"
					onClick={(e) => {
						e.stopPropagation();
						onCampClick?.(camp);
					}}
					style={{ cursor: 'pointer' }}
				/>
			</div>

			<div className="camp-content">
				<div className="camp-title-row">
					<div className="camp-name-container">
						<h3
							className="camp-title camp-title-clickable"
							onClick={() => {
								onCampClick?.(camp);
							}}
							style={{ cursor: 'pointer' }}
						>
							{camp.name}
						</h3>
						<p className="camp-organizer-name">{camp.organizer?.name}</p>
					</div>
				</div>
				
				<p className="camp-description">
					{camp.description && camp.description.length > 130 
						? `${camp.description.substring(0, 130).replace(/\s+\S*$/, '')}... ` 
						: camp.description + ' '
					}
					<a 
						href="#"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						onCampClick?.(camp);
					}}
					>
						more info
					</a>
				</p>

				<div className="camp-details">

					<div className="camp-detail-item">
						<Calendar className="icon icon--sm" />
						<CampDatesDropdown
							camp={camp}
							selectedDate={selectedDate}
							onDateChange={onDateChange}
							scheduledDateIds={scheduledDateIds}
						/>
					</div>
					<div className="camp-detail-item">
						<Clock className="icon icon--sm" />
						<span className="detail-text">Hours: {CampFormatUtils.formatTimeRange(camp.start_time, camp.end_time)}</span>
					</div>
					<div className="camp-detail-item">
						<Users className="icon icon--sm" />
						<span className="detail-text">Ages: {CampFormatUtils.formatAgeRange(camp.min_age, camp.max_age)}</span>
					</div>
					{camp.organizer?.google_rating && camp.organizer?.google_rating_count && camp.organizer?.google_maps_place_id && (
						<div className="camp-detail-item">
						<GoogleRating rating={camp.organizer?.google_rating} reviewCount={camp.organizer?.google_rating_count} googlePlacesId={camp.organizer?.google_maps_place_id} />
						</div>
					)}			
					<div className="camp-price-container camp-detail-item">
						<DollarSign className="icon icon--sm" />
						<span>Price: </span>
						<span className="camp-price">${priceParts.price}</span>
						{priceParts.timeframe && <span className="camp-price-timeframe"> / {priceParts.timeframe}</span>}
					</div>			
				</div>

				{/* Action Buttons */}
				<div className="camp-actions">

					{instantBookingEnabled && camp.organizer?.instant_booking ? (
						<button
							className="btn btn--instant-booking btn--sm"
							onClick={handleBookNow}
							disabled={isAtCapacity}
						>
							{isAtCapacity ? (
								'Camp Full'
							) : (
								<><Zap className="icon icon--sm" /> Book Now</>
							)}
						</button>
					) : (
						<button
							className="btn btn--primary btn--sm"
							onClick={handleBookNow}
							disabled={!camp.website}
						>
							Book Now <ExternalLink className="icon icon--sm" />
						</button>
					)}
					<button
						className="btn btn--secondary btn--sm"
						onClick={handleScheduleToggle}
						>
						{isEffectivelyScheduled ? 'Remove from Plan' : 'Add to Plan'} <CalendarCheck className="icon icon--primary icon--sm" />
					</button>

				</div>
			</div>

			{showRegistrationModal && (
				<LoginModalProvider initialStep="register">
					<LoginModal onClose={() => setShowRegistrationModal(false)} onLogin={handleLogin} onRegister={handleRegister} />
				</LoginModalProvider>
			)}

			<NewsletterSignupModal
				isOpen={showNewsletterModal}
				onClose={handleNewsletterModalClose}
				onSignupAndBook={handleNewsletterSignup}
				onSkipAndBook={handleSkipNewsletter}
				camp={camp}
			/>

		</div>
	);
};

export default CampCard;
